/* feeFormat.js — Iter-287
 *
 * Standardised commission / fee / revenue display helpers shared
 * by Deal Rooms, Forecasting Centre, Revenue cards and reports.
 *
 * Per the iter-287 brief:
 *
 *   • Agreed Fee — always displayed as `£XX,XXX + VAT`
 *     (e.g. `£23,125 + VAT`). Never "inc VAT" in the Deal Room.
 *   • Commission percentages — exactly 2 decimal places
 *     (`1.50%`, `1.75%`, `1.85%`, `2.00%`).
 *   • Currency values — GBP rounded to the nearest penny.
 *   • Revenue At Risk — Low / Medium / High consistently.
 *   • Expected Banking — `Month YYYY` (e.g. `May 2026`).
 *
 * Pure module, no React, deterministic, safe for tests. */

const _GBP = new Intl.NumberFormat("en-GB", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/* ─── Agreed Fee ─────────────────────────────────────────────────
 *
 * Renders the canonical "£23,125 + VAT" string.  The amount is
 * the agreed fee NET of VAT (operators capture net everywhere)
 * — the suffix communicates VAT applies.
 *
 * iter-alignment · P0 · Currency Formatting SSOT (2026-02-19) —
 * accepts an optional `vatIncluded` flag so a deal marked
 * VAT-inclusive renders "£23,125 inc VAT" instead of the
 * default "+ VAT" suffix.  Legacy callers passing a bare
 * amount continue to receive the "+ VAT" suffix unchanged. */
export function formatAgreedFee(amount, vatIncluded = false) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  // Round to the nearest penny; trim trailing zeros so whole
  // pounds read clean (`£23,125`) and non-whole values show
  // pence (`£23,125.50`).
  const rounded = Math.round(n * 100) / 100;
  const isWhole = Number.isInteger(rounded);
  const formatted = _GBP.format(rounded);
  // For whole pounds, _GBP renders without decimals; for
  // partial pence it renders to 2dp.
  const clean = isWhole
    ? formatted
    : Number(rounded).toLocaleString("en-GB", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
  const suffix = vatIncluded ? "inc VAT" : "+ VAT";
  return `£${clean} ${suffix}`;
}

/* ─── Agreed Fee resolver — canonical SSOT ───────────────────────
 *
 * iter-alignment · P0 · Agreed Fee Basis Reconciliation (2026-02-15)
 *
 * Every surface that shows a £ agreed fee (Revenue card,
 * Forecasting Centre, Command Centre, Mission Control) resolves
 * the fee amount NET-OF-VAT through this single helper so
 * percentage- and fixed-fee deals reconcile from the same
 * canonical calculation.  Prior to this helper the Revenue card
 * only read `deal.fee` and rendered `£0 + VAT` whenever the
 * canonical `feePct` + `agreedPrice` pair carried the SSOT (which
 * is the case for every percentage deal — Fee Basis Integrity
 * clears the stored fixed fee on save).
 *
 * Contract (net-of-VAT):
 *   • Percentage mode (`commissionType === "percent" | "percentage"`
 *     or `feePct` populated in the absence of a positive fixed
 *     fee) → `round(agreedPrice × feePct)` in pennies then
 *     divided by 100 so the ledger holds pennies without
 *     introducing IEEE-754 drift.
 *   • Fixed mode → the stored `fee` value, tolerant of pre-
 *     formatted "£9,500" strings and bare "9500" numeric
 *     strings alike.
 *
 * Both aliases are read for every field (snake_case backend +
 * camelCase pilot / chainStore).  Returns `null` when neither
 * source resolves to a positive finite number — callers pass
 * that straight through `formatAgreedFee()` which renders `—`.
 */
export function resolveAgreedFeeGBP(deal) {
  if (!deal || typeof deal !== "object") return null;
  const _num = (raw) => {
    if (raw == null || raw === "") return null;
    const n = typeof raw === "string"
      ? Number(raw.replace(/[^0-9.-]/g, ""))
      : Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const commissionType = String(
    deal.commissionType ?? deal.commission_type ?? ""
  ).toLowerCase();
  const feePct = _num(deal.feePct ?? deal.fee_pct);
  const fixedFee = _num(deal.fee);
  const agreedPrice = _num(
    deal.agreedPrice ?? deal.agreed_price ?? deal.price
  );
  // Explicit mode wins.  When commissionType is unset (legacy
  // pilot rows), a populated `feePct` with a zero / null fixed
  // fee is treated as percentage — mirrors `_detectMode` in
  // FinancialsSheet.jsx so both surfaces read the same signal.
  const modeIsPercent = commissionType === "percent"
    || commissionType === "percentage"
    || (!commissionType && feePct != null && (fixedFee == null || fixedFee <= 0));
  if (modeIsPercent) {
    if (feePct == null || agreedPrice == null || agreedPrice <= 0) return null;
    // (price × pct) → pennies → /100 → pounds.  Matches
    // `_computeCommissionGbp` in FinancialsSheet.jsx exactly.
    return Math.round(agreedPrice * feePct) / 100;
  }
  // Fixed mode.
  if (fixedFee != null && fixedFee > 0) return fixedFee;
  return null;
}


/* ─── Commission % ─────────────────────────────────────────────── */
export function formatCommissionPct(pct) {
  if (pct === null || pct === undefined) return "—";
  const n = Number(pct);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
}

/* ─── Currency (rounded to nearest penny) ───────────────────────
 *
 * General-purpose GBP renderer for non-fee surfaces (e.g.
 * "Fees at risk" totals on KPIs). Always returns a £-prefixed
 * string. */
export function formatGBP(amount) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n * 100) / 100;
  const isWhole = Number.isInteger(rounded);
  return `£${
    isWhole
      ? _GBP.format(rounded)
      : Number(rounded).toLocaleString("en-GB", {
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        })
  }`;
}

/* ─── Expected Banking month ────────────────────────────────────
 *
 * Accepts an ISO date string OR a "YYYY-MM" string OR a Date.
 * Returns "May 2026" — full month name + 4-digit year. */
export function formatBankingMonth(input) {
  if (!input) return "—";
  let d;
  if (input instanceof Date) {
    d = input;
  } else if (typeof input === "string") {
    // Tolerate "2026-05" form.
    const ym = /^(\d{4})-(\d{1,2})$/.exec(input);
    d = ym ? new Date(Number(ym[1]), Number(ym[2]) - 1, 1) : new Date(input);
  } else {
    return "—";
  }
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { month: "long", year: "numeric" });
}

/* ─── Revenue At Risk normaliser ────────────────────────────────
 *
 * Coerces any input into the canonical Low / Medium / High
 * vocabulary. Defaults to "Low" when unset. */
export function normaliseRevenueAtRisk(v) {
  const s = String(v || "").trim().toLowerCase();
  if (s === "high") return "High";
  if (s === "medium" || s === "med") return "Medium";
  return "Low";
}
